import {
  isWorkspaceAgentArtifactPillCitationType,
  isWorkspaceAgentEntityMonikerType,
  isWorkspaceAgentUnknownMonikerType,
  type WorkspaceAgentArtifactPillCitationType,
  type WorkspaceAgentEntityMonikerType,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentMonikerTypes';

/**
 * `@`, then `[` `(` or `{`, a type, `:`, the id, then `]` `)` or `}`.
 * The opener and closer do not have to match. The id stops at whitespace or the
 * first closer, so prose and a later `]` cannot swallow `@[type:id)`.
 * `searchFilter` and `unstructured` stay prose. Also used by Automations prompt
 * parsing (`automationRunPrompt.ts`). Each call builds its own `RegExp` so
 * consumers do not share `lastIndex`.
 */
const MONIKER_SOURCE = String.raw`@[\[({]([A-Za-z][A-Za-z0-9]*):([^\s\]\)}]+)[\]\)}]`;

export type PillMonikerSegment =
  | { type: 'text'; value: string }
  | {
      type: 'pill';
      /** Set when the moniker names a session artifact. Absent for `@[pill:<id>]` and entity monikers. */
      citationType?: WorkspaceAgentArtifactPillCitationType;
      /** Set when the id is the asset, version, board, or comment itself. */
      entityType?: WorkspaceAgentEntityMonikerType;
      monikerId: string;
      raw: string;
    }
  | { type: 'unknownMoniker'; monikerId: string; monikerType: string; raw: string };

const toPillMonikerSegment = (
  raw: string,
  monikerId: string,
  artifactType: string | undefined,
  entityType: string | undefined,
): PillMonikerSegment => {
  if (artifactType !== undefined && isWorkspaceAgentArtifactPillCitationType(artifactType)) {
    return { citationType: artifactType, monikerId, raw, type: 'pill' };
  }

  if (entityType !== undefined && isWorkspaceAgentEntityMonikerType(entityType)) {
    return { entityType, monikerId, raw, type: 'pill' };
  }

  return { monikerId, raw, type: 'pill' };
};

const segmentForMonikerType = (raw: string, monikerType: string, monikerId: string): PillMonikerSegment | undefined => {
  if (isWorkspaceAgentArtifactPillCitationType(monikerType)) {
    return toPillMonikerSegment(raw, monikerId, monikerType, undefined);
  }

  if (monikerType === 'pill' || isWorkspaceAgentEntityMonikerType(monikerType)) {
    return toPillMonikerSegment(
      raw,
      monikerId,
      undefined,
      isWorkspaceAgentEntityMonikerType(monikerType) ? monikerType : undefined,
    );
  }

  if (isWorkspaceAgentUnknownMonikerType(monikerType)) {
    return { monikerId, monikerType, raw, type: 'unknownMoniker' };
  }

  return undefined;
};

const splitMonikerSegments = (value: string): PillMonikerSegment[] => {
  const segments: PillMonikerSegment[] = [];
  const pattern = new RegExp(MONIKER_SOURCE, 'g');
  let lastIndex = 0;

  for (const match of value.matchAll(pattern)) {
    const start = match.index;
    const monikerType = match[1];
    const monikerId = match[2];

    if (start === undefined || monikerType === undefined || monikerId === undefined) {
      continue;
    }

    const segment = segmentForMonikerType(match[0], monikerType, monikerId);

    if (segment === undefined) {
      continue;
    }

    if (start > lastIndex) {
      segments.push({ type: 'text', value: value.slice(lastIndex, start) });
    }

    segments.push(segment);
    lastIndex = start + match[0].length;
  }

  if (lastIndex < value.length) {
    segments.push({ type: 'text', value: value.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: 'text', value }];
};

/** Shared by the plain-text renderer and the remark plugin, so both agree where a pill starts and ends. */
export const splitPillMonikers = (content: string): PillMonikerSegment[] => {
  if (content.length === 0) {
    return [];
  }

  return splitMonikerSegments(content);
};
